import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from 'react-i18next';
import { toast } from "../lib/toast";
import {
  Plus,
  Calendar,
  Trash2,
  Edit2,
  Search,
  X,
  Lock,
  Share2,
  Copy,
  FileText,
  ChevronDown,
  ArrowLeft,
  BookOpen,
  Map,
  CheckCircle,
  Users,
  Camera,
} from "lucide-react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MeetingsPageSkeleton, MeetingCardSkeleton } from "../components/LoadingShimmer";
import { format, parseISO } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import { useProject } from "../hooks/useProjects";
import { useMeetingsByProject } from "../hooks/useMeeting";
import * as meetingService from "../services/meeting.service";
import * as topicoService from "../services/topico.service";
import * as customizationService from "../services/customization.service";
import type { Meeting } from "../services/meeting.service";
import { ShareModal } from "../components/ShareModal";
import { ContatosDrawer } from "../components/ContatosDrawer";
import { FotoHistoricoDrawer } from "../components/FotoHistoricoDrawer";
import { useContatosProjeto } from "../hooks/useContatosProjeto";
import { CollaboratorIndicator } from "../components/CollaboratorIndicator";
import { DropdownMenu, DropdownItem } from "../components/DropdownMenu";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Header } from "../components/Header";
import { Input } from "../components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { usePageTitle } from "../hooks/usePageTitle";

type TabType = "all" | "draft" | "final";

export default function Meetings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation('common');

  // React Query hooks para cache e performance
  const { data: project, isLoading: isLoadingProject } = useProject(id || '');
  const { data: meetingsData, isLoading: isLoadingMeetings } = useMeetingsByProject(id || '');
  
  const meetings = meetingsData?.meetings || [];
  const permission = meetingsData?.permission || {
    level: "view" as const,
    canEdit: false,
    canDelete: false,
    canInvite: false,
    isOwner: false,
  };
  const canEdit = permission.canEdit;

  const loading = isLoadingProject || isLoadingMeetings;
  const projectName = project?.name || '';
  usePageTitle(projectName ? `Atas — ${projectName}` : 'Atas');

  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sharingMeeting, setSharingMeeting] = useState<Meeting | null>(null);
  const [cloningMeetingId, setCloningMeetingId] = useState<string | null>(null);
  const [showNewMeetingMenu, setShowNewMeetingMenu] = useState(false);
  const [showCloningShimmer, setShowCloningShimmer] = useState(false);
  const [deletingMeetingId, setDeletingMeetingId] = useState<string | null>(null);
  const [finalizeConfirm, setFinalizeConfirm] = useState<string | null>(null);
  const [showContatosDrawer, setShowContatosDrawer] = useState(false);
  const [showFotoHistorico, setShowFotoHistorico] = useState(false);
  const { contatos: contatosFromServer } = useContatosProjeto(id);
  const [contatos, setContatos] = useState<import("../services/contato.service").ContatoProjeto[]>([]);
  useEffect(() => { setContatos(contatosFromServer); }, [contatosFromServer]);

  // Redireciona se não tiver id ou user
  if (!id) {
    navigate("/dashboard");
    return null;
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  // Invalida cache de atas e projetos para refletir contadores no Dashboard sem F5
  const invalidateMeetingAndProjectCaches = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['meetings', 'project', id] }),
      queryClient.invalidateQueries({ queryKey: ['projects'] }),
    ]);
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    try {
      setDeletingMeetingId(meetingId);
      setDeleteConfirm(null);
      await meetingService.deleteMeeting(meetingId);
      toast.success(t('meetings.meeting_deleted'));
      await invalidateMeetingAndProjectCaches();
    } catch (error: any) {
      console.error("Erro:", error);
      const msg = error.message || '';
      if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        toast.error('Não foi possível excluir a ata — verifique sua conexão com a internet.');
      } else if (error.response?.status === 403) {
        toast.error('Você não tem permissão para excluir esta ata.');
      } else {
        toast.error(msg || 'Não foi possível excluir a ata. Tente novamente.');
      }
    } finally {
      setDeletingMeetingId(null);
    }
  };

  const createMeeting = async () => {
    try {
      if (!id) return;


      const nextNumber =
        meetings.length > 0
          ? Math.max(...meetings.map((m) => m.number)) + 1
          : 1;


      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;


      const newMeeting = await meetingService.createMeeting({
        project_id: id,
        number: nextNumber,
        date: dateString,
        location: "",
        status: "draft",
      });


      try {
        const customization = await customizationService.getUserCustomization();
        
        if (customization.default_topics && customization.default_topics.length > 0) {

          for (const topicName of customization.default_topics) {
            await topicoService.createTopico(newMeeting.id, {
              titulo: topicName,
            });
          }
        }
      } catch (error) {

        console.error('Erro ao criar tópicos padrão:', error);
      }

      toast.success(t('meetings.meeting_created'));
      await invalidateMeetingAndProjectCaches();
      navigate(`/projects/${id}/meetings/${newMeeting.id}`);
    } catch (error: any) {
      console.error("Erro:", error);
      const msg = error.message || '';
      if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        toast.error('Não foi possível criar a ata — verifique sua conexão com a internet.');
      } else {
        toast.error(msg || 'Não foi possível criar a ata. Tente novamente.');
      }
    }
  };

  const handleCloneMeeting = async (meetingId: string) => {
    try {
      if (!id) return;

      setCloningMeetingId(meetingId);
      setShowCloningShimmer(true);

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      const clonedMeeting = await meetingService.cloneMeeting(meetingId, {
        date: dateString,
      });

      toast.success(t('meetings.meeting_cloned'));
      await invalidateMeetingAndProjectCaches();
      setShowCloningShimmer(false);
      navigate(`/projects/${id}/meetings/${clonedMeeting.id}`);
    } catch (error: any) {
      console.error("Erro:", error);
      const msg = error.message || '';
      if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        toast.error('Não foi possível duplicar a ata — verifique sua conexão com a internet.');
      } else {
        toast.error(msg || 'Não foi possível duplicar a ata. Tente novamente.');
      }
      setShowCloningShimmer(false);
    } finally {
      setCloningMeetingId(null);
    }
  };

  const handleCloneLastMeeting = async () => {
    try {
      if (!id) return;


      const lastMeeting = meetings.reduce((latest, current) =>
        current.number > latest.number ? current : latest
      , meetings[0]);

      if (!lastMeeting) {
        toast.error(t('meetings.no_meetings_to_clone'));
        return;
      }

      setShowNewMeetingMenu(false);
      await handleCloneMeeting(lastMeeting.id);
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || 'Não foi possível duplicar a última ata. Tente novamente.');
    }
  };

  const handleFinalizeMeeting = async (meetingId: string) => {
    try {
      await meetingService.updateMeeting(meetingId, { status: "final" });
      toast.success(t('meetings.finalized_success'));
      await invalidateMeetingAndProjectCaches();
      setFinalizeConfirm(null);
    } catch (error: any) {
      console.error("Erro:", error);
      const msg = error.message || '';
      if (msg.includes('Network') || msg.includes('network') || msg.includes('ERR_NETWORK')) {
        toast.error('Não foi possível finalizar a ata — verifique sua conexão com a internet.');
      } else {
        toast.error(msg || 'Não foi possível finalizar a ata. Verifique os campos obrigatórios e tente novamente.');
      }
    }
  };

  if (loading) {
    return <MeetingsPageSkeleton />;
  }

  // Filtra meetings (já vem ordenado do hook)
  const filteredMeetings = meetings.filter((m) => {
    const matchesTab =
      activeTab === "all"
        ? true
        : activeTab === "draft"
        ? m.status === "draft"
        : activeTab === "final"
        ? m.status === "final"
        : true;

    const matchesSearch =
      searchQuery === ""
        ? true
        : m.number.toString().includes(searchQuery) ||
          (m.location && m.location.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (m.name && m.name.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesTab && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "system-ui" }}>
      <Header
        breadcrumbs={[
          { label: t('dashboard.title'), href: '/dashboard' },
          { label: projectName }
        ]}
      />

      {}
      <div className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Botão voltar */}
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-muted rounded-md transition-colors flex-shrink-0"
              aria-label="Voltar para projetos"
            >
              <ArrowLeft size={18} className="text-muted-foreground" />
            </button>

            {/* Info principal */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold text-foreground truncate">
                  {projectName}
                </h1>
                <span className="text-muted-foreground hidden sm:inline">•</span>
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  {meetings.length === 1
                    ? t('meetings.meetings_count_singular', { count: meetings.length })
                    : t('meetings.meetings_count_plural', { count: meetings.length })}
                </span>
              </div>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {}
              <button
                onClick={() => navigate(`/projects/${id}/mapa-atas`)}
                disabled={meetings.length === 0}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-background text-foreground border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Map size={16} />
                <span className="hidden sm:inline">{t('meetings.meeting_map')}</span>
              </button>

              {}
              <button
                onClick={() => navigate(`/projects/${id}/all-meetings-preview`)}
                disabled={meetings.length === 0}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-background text-foreground border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <BookOpen size={16} />
                <span className="hidden sm:inline">{t('meetings.export_all')}</span>
              </button>

              {/* Botão Contatos */}
              <button
                onClick={() => setShowContatosDrawer(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-background text-foreground border border-border rounded-md hover:bg-muted transition-colors"
              >
                <Users size={16} />
                <span className="hidden sm:inline">{t('meetings.contacts')}</span>
              </button>

              {/* Botão Histórico de Fotos */}
              <button
                onClick={() => setShowFotoHistorico(true)}
                disabled={meetings.length === 0}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-background text-foreground border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Camera size={16} />
                <span className="hidden sm:inline">{t('meetings.photos')}</span>
              </button>

              {/* Botão Nova Ata - só aparece se o usuário pode editar */}
              {canEdit && (
              <div className="relative">
                <button
                  onClick={() => setShowNewMeetingMenu(!showNewMeetingMenu)}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors shadow-sm"
                >
                  <Plus size={16} />
                  <span className="hidden sm:inline">{t('meetings.new_meeting')}</span>
                  <ChevronDown size={14} />
                </button>
              {showNewMeetingMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowNewMeetingMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-background rounded-md shadow-lg border border-border z-20">
                    <button
                      onClick={() => {
                        setShowNewMeetingMenu(false);
                        createMeeting();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
                    >
                      <FileText size={16} />
                      <span>{t('meetings.blank_meeting')}</span>
                    </button>
                    <button
                      onClick={handleCloneLastMeeting}
                      disabled={meetings.length === 0 || !!cloningMeetingId}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Copy size={16} />
                      <span>
                        {cloningMeetingId ? t('meetings.cloning') : t('meetings.clone_last')}
                      </span>
                    </button>
                  </div>
                </>
              )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex justify-between items-end mb-6">
          <div
            className="flex gap-1 border-b"
            style={{ borderColor: "#e5e5e5" }}
          >
            <button
              onClick={() => setActiveTab("all")}
              className="px-6 py-3 text-sm font-medium transition-colors"
              style={{
                color: activeTab === "all" ? "#013EFB" : "#737373",
                borderBottom:
                  activeTab === "all"
                    ? "2px solid #013EFB"
                    : "2px solid transparent",
                marginBottom: "-1px",
                borderRadius: 0,
              }}
            >
              {t('meetings.tab_all')}
            </button>
            <button
              onClick={() => setActiveTab("draft")}
              className="px-6 py-3 text-sm font-medium transition-colors"
              style={{
                color: activeTab === "draft" ? "#013EFB" : "#737373",
                borderBottom:
                  activeTab === "draft"
                    ? "2px solid #013EFB"
                    : "2px solid transparent",
                marginBottom: "-1px",
                borderRadius: 0,
              }}
            >
              {t('meetings.tab_draft')}
            </button>
            <button
              onClick={() => setActiveTab("final")}
              className="px-6 py-3 text-sm font-medium transition-colors"
              style={{
                color: activeTab === "final" ? "#013EFB" : "#737373",
                borderBottom:
                  activeTab === "final"
                    ? "2px solid #013EFB"
                    : "2px solid transparent",
                marginBottom: "-1px",
                borderRadius: 0,
              }}
            >
              {t('meetings.tab_final')}
            </button>
          </div>
          {meetings.length > 0 && (
            <div className="relative w-64">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10"
                size={18}
              />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('meetings.search_placeholder')}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        {filteredMeetings.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent className="pt-6">
              {searchQuery ? (
                <>
                  <Search
                    size={48}
                    className="mx-auto mb-4"
                    style={{ color: "#d1d5db" }}
                  />
                  <CardTitle className="text-lg mb-2">
                    {t('meetings.no_results')}
                  </CardTitle>
                  <CardDescription className="mb-4">
                    {t('meetings.try_other_terms')}
                  </CardDescription>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded"
                  >
                    {t('clear_search')}
                  </button>
                </>
              ) : (
                <>
                  <Calendar
                    size={48}
                    className="mx-auto mb-4"
                    style={{ color: "#d1d5db" }}
                  />
                  <CardDescription>
                    {activeTab === "all" && t('meetings.no_meetings')}
                    {activeTab === "draft" && t('meetings.no_draft')}
                    {activeTab === "final" && t('meetings.no_final')}
                  </CardDescription>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {showCloningShimmer && <MeetingCardSkeleton />}
            {filteredMeetings.map((meeting) =>
              deletingMeetingId === meeting.id ? (
                <MeetingCardSkeleton key={meeting.id} />
              ) : (
              <Card
                key={meeting.id}
                className="hover:bg-muted/50 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div
                      onClick={() => {
                        // Se não pode editar, vai para preview; caso contrário, abre o editor (mesmo que finalizada)
                        if (!canEdit) {
                          navigate(`/projects/${id}/meetings/${meeting.id}/preview`);
                        } else {
                          navigate(`/projects/${id}/meetings/${meeting.id}`);
                        }
                      }}
                      className="flex-1 cursor-pointer"
                    >
                      <CardTitle className="text-lg">
                        {meeting.name ? `${meeting.name} ${meeting.number}` : t('meetings.meeting_number', { number: meeting.number })}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {meeting.date && format(parseISO(meeting.date), "dd/MM/yyyy")}
                        {meeting.date && meeting.location && " - "}
                        {meeting.location || t('meetings.location_placeholder')}
                      </CardDescription>
                      <p className="text-sm mt-2" style={{ color: "#9ca3af" }}>
                        {meeting.created_at && (
                          <>
                            {t('meetings.created_at')}:{" "}
                            {new Date(meeting.created_at).toLocaleDateString(i18n.language, {
                              day: '2-digit', month: 'long', year: 'numeric'
                            })}
                          </>
                        )}
                        {meeting.created_at && meeting.updated_at && " | "}
                        {meeting.updated_at && (
                          <>
                            {t('meetings.modified_at')}:{" "}
                            {new Date(meeting.updated_at).toLocaleString(i18n.language, {
                              day: '2-digit', month: 'long', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CollaboratorIndicator
                        resourceType="minute"
                        resourceId={Number(meeting.id)}
                      />
                      <span
                        className="px-3 py-1 rounded text-sm font-medium"
                        style={{
                          backgroundColor:
                            meeting.status === "final" ? "#dcfce7" : "#fef3c7",
                          color:
                            meeting.status === "final" ? "#166534" : "#854d0e",
                        }}
                      >
                        {meeting.status === "final"
                          ? t('meetings.status_final')
                          : t('meetings.status_draft')}
                      </span>
                      <DropdownMenu>
                        {/* Editar - apenas se pode editar */}
                        {canEdit && (
                          meeting.status === "final" ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>
                                    <DropdownItem
                                      onClick={() => {}}
                                      icon={<Lock size={16} />}
                                      disabled
                                    >
                                      {t('edit')}
                                    </DropdownItem>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('meetings.final_cannot_edit')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <DropdownItem
                              onClick={() => navigate(`/projects/${id}/meetings/${meeting.id}`)}
                              icon={<Edit2 size={16} />}
                            >
                              {t('edit')}
                            </DropdownItem>
                          )
                        )}
                        {/* Clonar - apenas se pode editar */}
                        {canEdit && (
                          <DropdownItem
                            onClick={() => handleCloneMeeting(meeting.id)}
                            icon={<Copy size={16} />}
                            disabled={cloningMeetingId === meeting.id}
                          >
                            {cloningMeetingId === meeting.id ? t('meetings.cloning') : t('meetings.clone')}
                          </DropdownItem>
                        )}
                        {/* Exportar/Visualizar */}
                        <DropdownItem
                          onClick={() => navigate(`/projects/${id}/meetings/${meeting.id}/preview`)}
                          icon={<FileText size={16} />}
                        >
                          {t('meetings.export')}
                        </DropdownItem>
                        {/* Finalizar - apenas para atas em andamento e se pode editar */}
                        {canEdit && meeting.status !== "final" && (
                          <DropdownItem
                            onClick={() => setFinalizeConfirm(meeting.id)}
                            icon={<CheckCircle size={16} />}
                          >
                            {t('meetings.finalize')}
                          </DropdownItem>
                        )}
                        {/* Compartilhar - apenas se é dono */}
                        {permission.isOwner && (
                          <DropdownItem
                            onClick={() => setSharingMeeting(meeting)}
                            icon={<Share2 size={16} />}
                          >
                            {t('project.share')}
                          </DropdownItem>
                        )}
                        {/* Excluir - apenas se pode deletar */}
                        {permission.canDelete && (
                          <DropdownItem
                            onClick={() => setDeleteConfirm(meeting.id)}
                            icon={<Trash2 size={16} />}
                            danger
                          >
                            {t('delete')}
                          </DropdownItem>
                        )}
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {deleteConfirm && (
        <ConfirmDialog
          title={t('meetings.delete_meeting_title')}
          message={t('meetings.delete_meeting_message')}
          confirmText={t('delete')}
          cancelText={t('cancel')}
          onConfirm={() => handleDeleteMeeting(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
          type="danger"
        />
      )}

      {finalizeConfirm && (
        <ConfirmDialog
          title={t('meetings.finalize_confirm_title')}
          message={t('meetings.finalize_confirm_message')}
          confirmText={t('meetings.finalize')}
          cancelText={t('cancel')}
          onConfirm={() => handleFinalizeMeeting(finalizeConfirm)}
          onCancel={() => setFinalizeConfirm(null)}
          type="warning"
        />
      )}

      {sharingMeeting && (
        <ShareModal
          open={Boolean(sharingMeeting)}
          onClose={() => setSharingMeeting(null)}
          resourceType="minute"
          resourceId={Number(sharingMeeting.id)}
          resourceName={sharingMeeting.name ? `${sharingMeeting.name} ${sharingMeeting.number}` : t('meetings.default_name', { number: sharingMeeting.number })}
        />
      )}

      <ContatosDrawer
        open={showContatosDrawer}
        onClose={() => setShowContatosDrawer(false)}
        projetoId={id}
        canEdit={canEdit}
        contatos={contatos}
        onContatos={setContatos}
      />

      <FotoHistoricoDrawer
        open={showFotoHistorico}
        onClose={() => setShowFotoHistorico(false)}
        projetoId={id}
      />
    </div>
  );
}
